'use client';

/**
 * HomepageV3 SectionHeader — now consumes the published se3 design-system
 * package (`@startengine/design-system.se3.section-header`, exported as
 * `Se3SectionHeader`). Thin re-export adapter so existing local imports keep
 * working.
 *
 * Faithful port: identical props (eyebrow/title/desc/icon/action/data-cy),
 * identical default `data-cy` (`section-header`) plus the same sub-element
 * data-cy hooks (`section-header-eyebrow|title|desc|action`). The original
 * prop interface is re-declared here for type-safety (the package's generated
 * `.d.ts` uses loose `any` types).
 */
import type { FC, ReactNode } from 'react';
import { Se3SectionHeader } from '@startengine/design-system.se3.section-header';

export interface SectionHeaderAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface SectionHeaderProps {
  /** Tracked gold uppercase label with trailing hairline rule. */
  eyebrow: string;
  /** Optional serif title beneath the eyebrow. */
  title?: ReactNode;
  /** Optional supporting description line. */
  desc?: ReactNode;
  /** Optional leading icon rendered before the eyebrow text. */
  icon?: ReactNode;
  /** Optional "see all" style action link, right-aligned. */
  action?: SectionHeaderAction;
  /** Optional info tooltip next to the title (ordering disclosure). */
  tip?: string;
  className?: string;
  'data-cy'?: string;
}

const SectionHeader = Se3SectionHeader as unknown as FC<SectionHeaderProps>;

export default SectionHeader;
